import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, BookOpen, Languages, Headphones, Youtube } from "lucide-react";
import { trackClick } from "@/lib/api";
import { LANGUAGES } from "@/data/books";
import StoryAudio from "@/components/StoryAudio";

const BUY_LABEL = {
  EN: "Buy on Amazon",
  ES: "Comprar en Amazon",
  DE: "Bei Amazon kaufen",
  FR: "Acheter sur Amazon",
  IT: "Compra su Amazon",
  PT: "Comprar na Amazon",
};

const SPREAD_WORD = {
  EN: "Spread", ES: "Página", DE: "Doppelseite", FR: "Planche", IT: "Pagina", PT: "Página",
};
const OF_WORD = { EN: "of", ES: "de", DE: "von", FR: "sur", IT: "di", PT: "de" };

export default function QuickLook({ book, onClose }) {
  const [lang, setLang] = useState("EN");
  const [tab, setTab] = useState("listen");

  return (
    <AnimatePresence>
      {book && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <button
            aria-label="Close quick look"
            data-testid="quick-look-backdrop"
            className="absolute inset-0 cursor-default bg-emerald-ink/85 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={`Quick look inside ${book.title}`}
            data-testid="quick-look-modal"
            initial={{ y: 34, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 34, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[86vh] w-full max-w-4xl overflow-y-auto rounded-sm border border-gold/30 bg-emerald-deep shadow-lift"
          >
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid="quick-look-close"
              className="sticky right-0 top-0 z-10 float-right m-4 flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 bg-emerald-ink/80 text-cream/70 transition-colors hover:text-gold"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="px-6 pb-10 pt-4 sm:px-10">
              <div className="text-center">
                <p className="flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                  <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                  Inside Vol. {book.roman} · Panchatantra Tales
                </p>
                <AnimatePresence mode="wait">
                  <motion.h3
                    key={lang}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                    className="mt-3 font-serif text-3xl font-semibold text-cream sm:text-4xl"
                    data-testid="quick-look-title"
                  >
                    {book.titles[lang]}
                  </motion.h3>
                </AnimatePresence>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={lang}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="mt-2 font-serif text-lg italic text-gold-soft"
                    data-testid="quick-look-moral"
                  >
                    {book.morals[lang]}
                  </motion.p>
                </AnimatePresence>
              </div>

              <div
                className="mt-6 flex flex-wrap items-center justify-center gap-2"
                role="tablist"
                aria-label="Preview language"
                data-testid="quick-look-lang-tabs"
              >
                <Languages className="h-4 w-4 text-gold/70" aria-hidden="true" />
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    role="tab"
                    aria-selected={lang === l.code}
                    data-testid={`quick-look-lang-${l.code.toLowerCase()}`}
                    onClick={() => setLang(l.code)}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] transition-all duration-300 ${
                      lang === l.code
                        ? "border-gold bg-gold text-emerald-ink"
                        : "border-cream/20 text-cream/65 hover:border-gold/50 hover:text-gold"
                    }`}
                  >
                    <span aria-hidden="true">{l.flag}</span>
                    {l.code}
                  </button>
                ))}
              </div>

              <div
                className="mt-6 flex items-center justify-center gap-2"
                role="tablist"
                aria-label="Story preview format"
              >
                <button
                  role="tab"
                  aria-selected={tab === "listen"}
                  data-testid="quick-look-tab-listen"
                  onClick={() => setTab("listen")}
                  className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                    tab === "listen"
                      ? "border-gold bg-gold text-emerald-ink"
                      : "border-cream/20 text-cream/65 hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  <Headphones className="h-3.5 w-3.5" aria-hidden="true" />
                  Listen
                </button>
                <button
                  role="tab"
                  aria-selected={tab === "watch"}
                  data-testid="quick-look-tab-watch"
                  onClick={() => setTab("watch")}
                  className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                    tab === "watch"
                      ? "border-gold bg-gold text-emerald-ink"
                      : "border-cream/20 text-cream/65 hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  <Youtube className="h-3.5 w-3.5" aria-hidden="true" />
                  Watch
                </button>
              </div>

              {tab === "listen" ? (
                <StoryAudio
                  bookId={book.id}
                  lang={lang}
                  langName={LANGUAGES.find((l) => l.code === lang)?.name || "English"}
                />
              ) : (
                <div className="mx-auto mt-6 max-w-2xl" data-testid="quick-look-watch-panel">
                  {book.videos?.[lang] ? (
                    <div className="relative aspect-video w-full overflow-hidden rounded-sm border border-gold/40 bg-emerald-ink shadow-card">
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${book.videos[lang]}?rel=0`}
                        title={`${book.titles[lang]} — narrated fable film`}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 h-full w-full"
                        data-testid="quick-look-video"
                      />
                    </div>
                  ) : (
                    <div className="border border-dashed border-gold/30 bg-emerald-ink/50 px-8 py-10 text-center">
                      <Youtube className="mx-auto h-8 w-8 text-gold/70" aria-hidden="true" />
                      <p className="mt-4 font-serif text-lg italic text-cream/85">
                        The {LANGUAGES.find((l) => l.code === lang)?.name} fable film is coming soon.
                      </p>
                      <a
                        href="https://www.youtube.com/channel/UCIOdYZDaejW4qKL9eljLfmA"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="quick-look-youtube-link"
                        onClick={() =>
                          trackClick(
                            `quicklook-youtube-${book.id}`,
                            "https://www.youtube.com/channel/UCIOdYZDaejW4qKL9eljLfmA",
                            "quick-look"
                          )
                        }
                        className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-gold underline-offset-4 transition-colors hover:text-gold-amber hover:underline"
                      >
                        Watch the latest fable on our YouTube channel
                      </a>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-10 space-y-12">
                {book.spreads.map((s, i) => (
                  <div
                    key={i}
                    className="grid items-center gap-6 md:grid-cols-2 md:gap-10"
                    data-testid={`quick-look-spread-${i + 1}`}
                  >
                    <img
                      src={s.image}
                      alt={`Interior illustration — ${s.heading[lang]}`}
                      loading="lazy"
                      className={`w-full rounded-sm border border-gold/25 object-cover shadow-card ${
                        i % 2 === 1 ? "md:order-2" : ""
                      }`}
                    />
                    <div className={i % 2 === 1 ? "md:order-1" : ""}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/70">
                        {SPREAD_WORD[lang]} {i + 1} {OF_WORD[lang]} {book.spreads.length}
                      </p>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={lang}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25 }}
                        >
                          <h4 className="mt-3 font-serif text-2xl font-semibold text-cream">
                            {s.heading[lang]}
                          </h4>
                          <p className="mt-3 text-base leading-relaxed text-cream/70">
                            {s.text[lang]}
                          </p>
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 flex flex-col items-center gap-3 border-t border-gold/15 pt-8">
                <a
                  href={book.amazon}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="quick-look-buy-btn"
                  onClick={() => trackClick(`quicklook-${book.id}`, book.amazon, "quick-look")}
                  className="flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-glow"
                >
                  <ShoppingBag className="h-4 w-4" />
                  {BUY_LABEL[lang]} — {book.titles[lang]}
                </a>
                <p className="text-xs text-cream/50">
                  Available in English, Español, Deutsch, Français, Italiano & Português
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
